import { useEffect, useState } from 'react';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Modal } from '../ui/Modal.js';
import { useUi } from '../stores/ui.js';
import { saveLafourcheCredentials } from '../api/carrefour.js';
import { errorMessage } from '../lib/errors.js';

/**
 * Le pendant de `CarrefourCookiesModal` pour La Fourche
 * ([ADR 0012](../../../docs/adr/0012-scraper-lafourche-sans-navigateur.md)).
 *
 * Deux champs, et deux natures différentes : le **refresh token** est le secret durable qui
 * donne accès au compte ; l'**identifiant de panier** n'est pas un secret, c'est l'hypothèse
 * de la V1 — il disparaîtra le jour où le runner le résoudra tout seul.
 *
 * Rien n'est relu : l'API écrit en SSM, seul le scraper lit. Un champ laissé vide n'écrase
 * pas ce qui est déjà enregistré.
 */
export function LafourcheCredentialsModal(): React.ReactElement | null {
  const open = useUi((s) => s.lafourcheCredentialsOpen);
  const close = useUi((s) => s.closeLafourcheCredentials);

  const [refreshToken, setRefreshToken] = useState('');
  const [cartId, setCartId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRefreshToken('');
      setCartId('');
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  if (!open) return null;

  async function save(): Promise<void> {
    if (submitting) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const payload: { refreshToken?: string; cartId?: string } = {};
      if (refreshToken.trim()) payload.refreshToken = refreshToken.trim();
      if (cartId.trim()) payload.cartId = cartId.trim();
      const r = await saveLafourcheCredentials(payload);
      const saved = [r.refreshToken && 'jeton', r.cartId && 'panier'].filter(Boolean);
      setSuccess(`${saved.join(' et ')} enregistré${saved.length > 1 ? 's' : ''}`);
      setRefreshToken('');
      setCartId('');
      setTimeout(() => close(), 1200);
    } catch (e) {
      setError(errorMessage(e, 'Erreur inconnue'));
    } finally {
      setSubmitting(false);
    }
  }

  const nothingToSave = !refreshToken.trim() && !cartId.trim();

  return (
    <Modal
      open={open}
      onClose={close}
      title="Identifiants La Fourche"
      closeLabel="Fermer"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Annuler
          </Button>
          <Button onClick={save} disabled={nothingToSave} loading={submitting}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="pile">
        <div className="champ">
          <label className="champ-libelle" htmlFor="lf-token">
            Refresh token Firebase
          </label>
          <ol className="discret pl-5 list-decimal space-y-1">
            <li>
              Ouvre <span className="text-fg">lafourche.fr</span> connecté, puis DevTools →
              Application → IndexedDB → <code className="code">firebaseLocalStorageDb</code>
            </li>
            <li>
              Copie <code className="code">stsTokenManager.refreshToken</code>
            </li>
          </ol>
          <Input
            id="lf-token"
            type="password"
            value={refreshToken}
            onChange={(e) => setRefreshToken(e.target.value)}
            placeholder="Laisser vide pour ne pas le changer"
            autoComplete="off"
            className="font-mono"
          />
        </div>

        <div className="champ">
          <label className="champ-libelle" htmlFor="lf-cart">
            Identifiant de panier
          </label>
          <p className="champ-aide">
            Dans la console de la même page :{' '}
            <code className="code">localStorage.getItem(&apos;shoppingCartId&apos;)</code>. À remettre à
            jour si un run échoue sur « panier introuvable ».
          </p>
          <Input
            id="lf-cart"
            value={cartId}
            onChange={(e) => setCartId(e.target.value)}
            placeholder="Laisser vide pour ne pas le changer"
            autoComplete="off"
            className="font-mono"
          />
        </div>

        <p className="champ-erreur" role="alert">
          {error}
        </p>
        {success && (
          <p className="alerte alerte-succes alerte-sobre" role="status">
            {success}
          </p>
        )}
      </div>
    </Modal>
  );
}
